/**
 * Carregador da API do Google Maps
 * 
 * Este módulo fornece funções para carregar a API do Google Maps
 * dinamicamente e gerencia a inicialização da biblioteca.
 * 
 * Também inclui tratamento para erros de autenticação da API
 * e dispara eventos para notificar outros componentes.
 */
import { getGoogleApiKeys } from '@/services/chaveGoogleService';

// Estado do carregamento da API
let googleMapsPromise = null;
let apiKey = null;
let isApiError = false;

// nosso pool de chaves e índice
let apiKeys = [];
let badApiKeys = [];
let currentIndex = 0;

/**
 * Carrega o script da API do Google Maps
 * @returns {Promise} Promise que resolve quando a API estiver carregada
 */
export const loadGoogleMapsApi = async () => {
  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  // Verificar se já houve um erro de autenticação e evitar carregamento repetido
  if (isApiError) {
    return Promise.reject(new Error('Google Maps API authentication failed'));
  }

  // carrega todas as chaves na primeira vez
  if (apiKeys.length === 0) {
    apiKeys = await getGoogleApiKeys();
  }

  // round-robin: escolhe próxima chave que não esteja em badApiKeys
  const start = currentIndex;
  let candidate;
  do {
    candidate = apiKeys[currentIndex % apiKeys.length];
    currentIndex++;
  } while (badApiKeys.includes(candidate) && (currentIndex % apiKeys.length) !== (start % apiKeys.length));

  if (badApiKeys.includes(candidate)) {
    return Promise.reject(new Error('No valid Google Maps API keys available'));
  }

  apiKey = candidate;
  googleMapsPromise = new Promise((resolve, reject) => {
    // Se o Google Maps já estiver carregado, resolva imediatamente
    if (window.google && window.google.maps) {
      resolve(window.google.maps);
      return;
    }

    // Adiciona um handler global para erros de autenticação
    window.gm_authFailure = () => {
      badApiKeys.push(apiKey);
      isApiError = true;
      googleMapsPromise = null;
      document.dispatchEvent(new CustomEvent('google-maps-auth-error', { detail: { apiKey } }));
    };

    // Callback global que será chamada quando o script for carregado
    window.initGoogleMaps = () => {
      if (window.google && window.google.maps) {
        resolve(window.google.maps);
      } else {
        reject(new Error('Google Maps API não foi carregada corretamente'));
      }
      delete window.initGoogleMaps;
    };

    // Cria o elemento script com carregamento assíncrono
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps&language=pt-BR`;
    script.async = true;
    script.defer = true;
    
    // Em caso de falha no carregamento
    script.onerror = () => {
      reject(new Error('Falha ao carregar Google Maps API'));
      googleMapsPromise = null;
    };

    // Adiciona o script à página
    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

// Função para obter a chave da API atual (para mensagens de erro)
export const getCurrentApiKey = () => apiKey;

/**
 * Inicializa o serviço de Autocomplete do Google Maps
 * @returns {Promise<google.maps.places.AutocompleteService>} Instância do serviço de autocomplete
 */
export const createAutocompleteService = async () => {
  await loadGoogleMapsApi();
  return new window.google.maps.places.AutocompleteService();
};

/**
 * Inicializa o serviço de Places do Google Maps para obter detalhes de um lugar
 * @returns {Promise<google.maps.places.PlacesService>} Instância do serviço de places
 */
export const createPlacesService = async () => {
  await loadGoogleMapsApi();
  // Precisamos de um elemento DOM para criar o serviço de places
  const el = document.createElement('div');
  document.body.appendChild(el);
  return new window.google.maps.places.PlacesService(el);
};

/**
 * Verifica se houve um erro de autenticação da API
 * @returns {boolean} true se houve erro de autenticação
 */
export const hasApiError = () => isApiError;

/**
 * Reset the API loading state to allow retry after page refresh
 */
export const resetApiLoadingState = () => {
  // limpa apenas o carregamento para forçar novo script, mantém badApiKeys
  googleMapsPromise = null;
  isApiError = false;

  // Remove referências globais para forçar reload completo
  if (window.google && window.google.maps) {
    delete window.google.maps;
  }
  if (window.google) {
    delete window.google;
  }

  // Remove eventuais scripts antigos da Google Maps API
  document
    .querySelectorAll('script[src*="maps.googleapis.com/maps/api/js"]')
    .forEach(tag => tag.remove());
};

export default {
  loadGoogleMapsApi,
  getCurrentApiKey,
  createAutocompleteService,
  createPlacesService,
  hasApiError,
  resetApiLoadingState
};
